/**
 * useCrypto -- Zustand store for the Crypto Dashboard.
 * Fetches dashboard overview, coin details, DeFi, exchanges, whales, correlation.
 *
 * Debouncing: won't re-fetch if the same data was fetched less than 30 seconds ago.
 * Stale data: keeps last known data on error / during background refresh.
 */
import { create } from 'zustand'
import {
  getCryptoDashboard,
  getCryptoCoinDetail,
  getCryptoDefi,
  getCryptoExchanges,
  getCryptoWhaleActivity,
  getCryptoCorrelation,
} from '@/lib/api'
import type {
  CryptoDashboard,
  CryptoCoinDetail,
  CryptoDefiResponse,
  CryptoExchangesResponse,
  WhaleActivityResponse,
  CryptoCorrelationResponse,
} from '@/lib/types'
import { useEffect } from 'react'

// Minimum milliseconds between full refreshes
const DEBOUNCE_MS = 30_000

interface CryptoState {
  dashboard: CryptoDashboard | null
  coinDetail: CryptoCoinDetail | null
  defi: CryptoDefiResponse | null
  exchanges: CryptoExchangesResponse | null
  whales: WhaleActivityResponse | null
  correlation: CryptoCorrelationResponse | null

  loading: boolean
  refreshing: boolean   // background refresh (stale data still visible)
  error: string | null
  coinLoading: boolean

  _lastFetchedAt: number  // epoch ms of last successful fetchAll

  fetchDashboard: () => Promise<void>
  fetchCoinDetail: (coinId: string) => Promise<void>
  fetchDefi: () => Promise<void>
  fetchExchanges: () => Promise<void>
  fetchWhales: () => Promise<void>
  fetchCorrelation: () => Promise<void>
  fetchAll: (force?: boolean) => Promise<void>
}

export const useCryptoStore = create<CryptoState>()((set, get) => ({
  dashboard: null,
  coinDetail: null,
  defi: null,
  exchanges: null,
  whales: null,
  correlation: null,
  loading: false,
  refreshing: false,
  error: null,
  coinLoading: false,
  _lastFetchedAt: 0,

  fetchDashboard: async () => {
    set({ loading: true, error: null })
    try {
      const data = await getCryptoDashboard()
      set({ dashboard: data, loading: false })
    } catch (e) {
      set({ error: String(e), loading: false })
    }
  },

  fetchCoinDetail: async (coinId: string) => {
    set({ coinLoading: true })
    try {
      const data = await getCryptoCoinDetail(coinId)
      set({ coinDetail: data, coinLoading: false })
    } catch (e) {
      set({ coinLoading: false })
    }
  },

  fetchDefi: async () => {
    try {
      const data = await getCryptoDefi()
      set({ defi: data })
    } catch (_) { /* swallow */ }
  },

  fetchExchanges: async () => {
    try {
      const data = await getCryptoExchanges()
      set({ exchanges: data })
    } catch (_) { /* swallow */ }
  },

  fetchWhales: async () => {
    try {
      const data = await getCryptoWhaleActivity()
      set({ whales: data })
    } catch (_) { /* swallow */ }
  },

  fetchCorrelation: async () => {
    try {
      const data = await getCryptoCorrelation()
      set({ correlation: data })
    } catch (_) { /* swallow */ }
  },

  fetchAll: async (force = false) => {
    const now = Date.now()
    const state = get()

    // Debounce: skip if data is fresh and not forced
    if (!force && state._lastFetchedAt > 0 && now - state._lastFetchedAt < DEBOUNCE_MS) {
      return
    }

    // If we already have data, show refreshing indicator instead of blanking the page
    const hasData = state.dashboard !== null
    if (hasData) {
      set({ refreshing: true, error: null })
    } else {
      set({ loading: true, error: null })
    }

    try {
      const [dashboard, defi, exchanges, whales, correlation] = await Promise.all([
        getCryptoDashboard(),
        getCryptoDefi(),
        getCryptoExchanges(),
        getCryptoWhaleActivity(),
        getCryptoCorrelation(),
      ])
      set({
        dashboard,
        defi,
        exchanges,
        whales,
        correlation,
        loading: false,
        refreshing: false,
        error: null,
        _lastFetchedAt: Date.now(),
      })
    } catch (e) {
      // On error, keep existing data visible — only update error flag
      set({ error: String(e), loading: false, refreshing: false })
    }
  },
}))

/** Drop-in hook: fetches all crypto data on mount, auto-refreshes every 30s. */
export function useCryptoWithRefresh(intervalMs = 30_000) {
  const store = useCryptoStore()

  useEffect(() => {
    store.fetchAll()
    const id = setInterval(() => store.fetchAll(), intervalMs)
    return () => clearInterval(id)
  }, [intervalMs]) // eslint-disable-line

  return store
}
